import { Box, Button, Chip, TextField, Typography } from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";

interface Variable {
  label: string;
  value: string;
  color: string;
  hoverColor: string;
}

interface FormulaBuilderProps {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  variables: Variable[];
}

const FormulaBuilder = ({
  label,
  value,
  onChange,
  variables,
}: FormulaBuilderProps) => {
  const insertText = (text: string) => {
    const currentValue = value || "";
    onChange(currentValue + text);
  };

  const handleDelete = () => {
    const currentValue = value || "";
    if (!currentValue) return;

    let newValue = currentValue;
    const lastChar = currentValue[currentValue.length - 1];

    if (/[A-Z_]/.test(lastChar)) {
      // Remove variable
      let startIndex = currentValue.length - 1;
      while (startIndex > 0 && /[A-Z_]/.test(currentValue[startIndex - 1])) {
        startIndex--;
      }
      newValue = currentValue.substring(0, startIndex);
    } else {
      // Remove 1 char (number, operator)
      newValue = currentValue.substring(0, currentValue.length - 1);
    }

    // Remove spaces
    newValue = newValue.trimEnd();

    onChange(newValue);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        {variables.map((variable) => (
          <Chip
            key={variable.value}
            label={variable.label}
            onClick={() => insertText(variable.value)}
            sx={{
              fontWeight: 500,
              bgcolor: variable.color,
              cursor: "pointer",
              "&:hover": { bgcolor: variable.hoverColor },
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        {["+", "-", "*", "/", "(", ")"].map((op) => (
          <Button
            key={op}
            variant="outlined"
            size="small"
            onClick={() => insertText(` ${op} `)}
            sx={{ minWidth: 40, fontWeight: 700 }}
          >
            {op}
          </Button>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
        {["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "."].map((num) => (
          <Button
            key={num}
            variant="outlined"
            size="small"
            onClick={() => insertText(num)}
            sx={{ minWidth: 40, fontWeight: 600 }}
          >
            {num}
          </Button>
        ))}
        <Button
          key="delete"
          variant="outlined"
          color="error"
          size="small"
          onClick={handleDelete}
          sx={{ minWidth: 40, fontWeight: 700 }}
        >
          <BackspaceIcon />
        </Button>
      </Box>
      <TextField
        value={value || ""}
        fullWidth
        multiline
        rows={2}
        aria-readonly
        sx={{
          "& .MuiInputBase-input": {
            cursor: "default",
          },
        }}
      />
    </Box>
  );
};

export default FormulaBuilder;
